import { buildReducers, createLazyList, createRequestStatusObject, RequestStatus, RequestStatusObject } from '../utils';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../store';
import { API } from 'aws-amplify';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';
import { Coupon, Product } from '../../types/products';
import { LazyList, LazyListRequest, LazyListResponse } from '../../types/types';
import { UserAccount, UserLedger, UserMailchimp } from '../../types/user';

export type LeadShareCurrency = 'GBP' | 'EUR' | 'USD';

/**
 * Structured consent copy slots (PTEC-562). The disclosure block is never a
 * slot — it is generated from fieldSet so wording cannot drift from what
 * transmits. `consentCopy` on the enrollment is the server-rendered result.
 */
export interface ConsentTemplate {
	headline: string;
	blurb: string;
	questionPurpose: string;
	ctaYes: string;
	ctaNo: string;
	privacyNoticeUrl: string;
}

export interface LeadShareEnrollment {
	organisationId: string;
	enabled: boolean;
	consentTemplate: ConsentTemplate;
	consentCopy: string;
	consentCopyVersion: number;
	fieldSet: string[];
	fieldSetVersion: number;
	placement: 'dashboard' | 'firstLogin';
	audienceRules: {
		mode: 'excludeAll' | 'dataSubjects' | 'guardians' | 'all';
		// PTEC-568 audience "pill" — two independent, unionable mechanisms.
		selfSelect?: { enabled: boolean; interestKeys: string[] };
		targeted?: { enabled: boolean; criteria: unknown[] };
	};
	/** PTEC-568: allowlist — may this partner's opportunity ever render to a minor? Default false. */
	eligibleForMinors?: boolean;
	deliveryMode: 'sandbox' | 'live';
	endpointRef: string;
	credentialsRef: string;
	pricePerLead: number;
	currency: LeadShareCurrency;
	updatedBy?: string;
	updatedAt?: string;
}

export type LeadShareEnrollmentUpdate = Partial<
	Omit<
		LeadShareEnrollment,
		'organisationId' | 'consentCopy' | 'consentCopyVersion' | 'fieldSetVersion' | 'updatedBy' | 'updatedAt'
	>
>;

/** Per-row summary attached to the partner-eligible organisations list. */
export interface LeadShareSummary {
	configured: boolean;
	enabled: boolean;
	fieldSet: string[];
	pricePerLead: number;
	currency: LeadShareCurrency;
	updatedAt: string | null;
	updatedBy: string | null;
}

/** Lifecycle state from the PARTNERENROLLMENT record (PTEC-549). */
export interface PartnerEnrollmentSummary {
	status: string;
	enrolledAt?: string;
	updatedAt?: string;
	updatedBy?: string;
}

export interface AdminState {
	organisations: LazyList<any>;
	partners: LazyList<any>;
	partnersRequestStatus: RequestStatusObject;
	organisationGrants: any;
	organisationOrderHistory: LazyList<any>;
	orderHistory: LazyList<any>;
	orderInvoicePDF: any;
	organisationGrantsRequestStatus: RequestStatusObject;
	addDigitalProductStatus: RequestStatusObject;
	addDigitalCouponStatus: RequestStatusObject;
	resetUserMFAStatus: RequestStatusObject;
	setTempPasswordStatus: RequestStatusObject;
	userAccount: UserAccount | null;
	userAccountStatus: RequestStatusObject;
	/** The account request the page is waiting on; an answer to any earlier one is ignored. */
	userAccountRequestId: string | null;
	resendUserInviteStatus: RequestStatusObject;
	retryLedgerStatus: RequestStatusObject;
	retryMailchimpStatus: RequestStatusObject;
	editDigitalProductStatus: RequestStatusObject;
	deleteDigitalProductStatus: RequestStatusObject;
	deleteDigitalCouponStatus: RequestStatusObject;
	organisationRequestStatus: RequestStatusObject;
	organisationOrderHistoryRequestStatus: RequestStatusObject;
	orderHistoryRequestStatus: RequestStatusObject;
	updateOrganisationGrantStatus: RequestStatusObject;
	changeOrganisationLicenceStatus: RequestStatusObject;
	updateOrderInvoiceStatus: RequestStatusObject;
	orderInvoicePDFRequestStatus: RequestStatusObject;
	leadShareEnrollment: LeadShareEnrollment | null;
	leadShareEnrollmentRequestStatus: RequestStatusObject;
	leadShareLedger: LeadShareLedger | null;
	leadShareLedgerRequestStatus: RequestStatusObject;
	leadShareBillingSummary: LeadShareBillingSummary | null;
	leadShareBillingSummaryRequestStatus: RequestStatusObject;
	updateLeadShareEnrollmentStatus: RequestStatusObject;
	isLoading?: boolean;
}

const initialState: AdminState = {
	organisations: createLazyList(),
	partners: createLazyList(),
	partnersRequestStatus: createRequestStatusObject(),
	organisationGrants: createLazyList(),
	organisationOrderHistory: createLazyList(),
	orderHistory: createLazyList(),
	orderInvoicePDF: null,
	organisationGrantsRequestStatus: createRequestStatusObject(),
	addDigitalProductStatus: createRequestStatusObject(),
	addDigitalCouponStatus: createRequestStatusObject(),
	resetUserMFAStatus: createRequestStatusObject(),
	setTempPasswordStatus: createRequestStatusObject(),
	userAccount: null,
	userAccountStatus: createRequestStatusObject(),
	userAccountRequestId: null,
	resendUserInviteStatus: createRequestStatusObject(),
	retryLedgerStatus: createRequestStatusObject(),
	retryMailchimpStatus: createRequestStatusObject(),
	editDigitalProductStatus: createRequestStatusObject(),
	deleteDigitalProductStatus: createRequestStatusObject(),
	deleteDigitalCouponStatus: createRequestStatusObject(),
	organisationRequestStatus: createRequestStatusObject(),
	organisationOrderHistoryRequestStatus: createRequestStatusObject(),
	orderHistoryRequestStatus: createRequestStatusObject(),
	updateOrganisationGrantStatus: createRequestStatusObject(),
	changeOrganisationLicenceStatus: createRequestStatusObject(),
	updateOrderInvoiceStatus: createRequestStatusObject(),
	orderInvoicePDFRequestStatus: createRequestStatusObject(),
	leadShareEnrollment: null,
	leadShareEnrollmentRequestStatus: createRequestStatusObject(),
	leadShareLedger: null,
	leadShareLedgerRequestStatus: createRequestStatusObject(),
	leadShareBillingSummary: null,
	leadShareBillingSummaryRequestStatus: createRequestStatusObject(),
	updateLeadShareEnrollmentStatus: createRequestStatusObject(),
	isLoading: false
};

export const getOrderInvoicePDF = createAsyncThunk<
	any,
	{ orderId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getOrderInvoicePDF', async ({ orderId }) => {
	try {
		const response = await API.get('inspirusapi', `/admins/orders/${orderId}/order-pdf`, {
			headers: {},
			response: true
		});

		return response.data;
	} catch (error: unknown) {
		return 'Failed to get order invoice PDF.';
	}
});

export const updateOrderInvoiceStatus = createAsyncThunk<
	any,
	{ orderId: string; status: string; invoiceNumber?: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/updateOrderInvoiceStatus', async ({ orderId, status, invoiceNumber }) => {
	try {
		const response = await API.put('inspirusapi', `/admins/organisations/orders/${orderId}`, {
			headers: {},
			body: { status, invoiceNumber }
		});

		return response;
	} catch (error: unknown) {
		return 'Failed to update order invoice status.';
	}
});

export const getOrganisationOrderHistory = createAsyncThunk<
	LazyListResponse<any>,
	{ organisationId: string; payload: LazyListRequest },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getOrganisationOrderHistory', async ({ organisationId, payload }) => {
	try {
		const { next, limit } = payload;
		const response = await API.get('inspirusapi', `/admins/organisations/${organisationId}/orders`, {
			queryStringParameters: {
				next,
				limit
			},
			headers: {},
			response: true
		});

		return response.data;
	} catch (error: unknown) {
		return 'Failed to get organization order history.';
	}
});

export const getOrderHistory = createAsyncThunk<
	LazyListResponse<any>,
	{ payload: LazyListRequest; status?: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getOrderHistory', async ({ payload, status }) => {
	try {
		const { next, limit } = payload;
		const response = await API.get('inspirusapi', '/admins/organisations/orders', {
			queryStringParameters: {
				next,
				limit,
				status
			},
			headers: {},
			response: true
		});

		return response.data;
	} catch (error: unknown) {
		return 'Failed to get organization order history.';
	}
});

export const getOrganisations = createAsyncThunk<
	LazyListResponse<any>,
	{ payload: LazyListRequest; filter?: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getOrganisations', async ({ payload, filter }) => {
	const { next, limit } = payload;
	try {
		const response = await API.get('inspirusapi', '/admins/organisations', {
			queryStringParameters: { next, limit, type: filter },
			headers: {},
			response: true
		});

		return response.data;
	} catch (error: unknown) {
		console.error('Error in getOrganisations:', error);
		return 'Failed to get organisations.';
	}
});

/** Loads every partner-eligible organisation page — the enrol dialog searches the list client-side. */
export const getPartnerEligibleOrganisations = createAsyncThunk<
	LazyListResponse<any>,
	void,
	{ dispatch: AppDispatch; state: RootState }
>('admin/getPartnerEligibleOrganisations', async () => {
	const items: any[] = [];
	let next: string | undefined;
	do {
		const response = await API.get('inspirusapi', '/admins/partners/eligible-organisations', {
			queryStringParameters: { limit: 200, next },
			headers: {},
			response: true
		});
		items.push(...(response.data.items ?? []));
		next = response.data.next ?? undefined;
	} while (next);

	return { items, totalCount: items.length, next: null };
});

export const updateOrganisationGrant = createAsyncThunk<
	any,
	{ grantId: string; grant: any },
	{ dispatch: AppDispatch; state: RootState }
>('admin/updateOrganisationGrant', async ({ grantId, grant }) => {
	// Deliberately unguarded: catching here resolved the thunk with an error *string*,
	// which counts as fulfilled, so a failed write ran the success reducer and told the
	// admin 'grants updated successfully'. Letting it reject routes it to `onRejected`,
	// which already raises `updateErrorMessage`.
	const response = await API.put('inspirusapi', `/admins/organisations/grants/${grantId}`, {
		headers: {},
		body: grant
	});

	return response;
});

/** How an unpaid organisation gets a new licence (PTEC-977). */
export type LicenceAccessMode = 'grantNow' | 'awaitPayment';

/**
 * How the allowances land: `exact` sets each to the new licence's value, `keepExtras`
 * keeps whatever the organisation holds beyond its current licence.
 */
export type LicenceAllowanceMode = 'exact' | 'keepExtras';

export interface LicenceAllowanceChange {
	key: string;
	before: number;
	after: number;
}

/** What a licence change does, as returned by PUT /admins/organisations/:id/license. */
export interface LicenceChangeSummary {
	organisationId: string;
	from: { license: string | null; licenseId: string | null };
	to: { license: string; licenseId: string };
	restriction: 'awaitingFirstPayment' | 'overdue' | 'pendingChange' | null;
	pendingChange: { license: string; licenseId: string } | null;
	outstandingOrder: {
		id: string;
		status: string | null;
		price: number | null;
		currency: string | null;
		creationDateTime: string | null;
	} | null;
	canAwaitPayment: boolean;
	allowanceMode: LicenceAllowanceMode;
	/** The chosen mode's outcome. */
	allowances: LicenceAllowanceChange[];
	allowanceOptions: Record<LicenceAllowanceMode, LicenceAllowanceChange[]>;
	/** What keeping extras gives over the exact licence; empty when the two agree. */
	extras: { key: string; amount: number }[];
	featuresAdded: string[];
	rolesAdded: string[];
	movesVerifierOnlyRoles: boolean;
	preview?: boolean;
	accessMode?: LicenceAccessMode;
	applied?: boolean;
	grants?: any;
	rolesMoved?: number;
	roleMoveFailed?: boolean;
}

const LICENCE_CHANGE_KEYS = 'pages.admin.organisations.licenceChange';

/**
 * The translated message for a licence-change refusal. The API answers with a `code`
 * (organisationLicence.js in the admins function); anything unrecognised gets the
 * generic message, or the preview one when it was a preview that failed.
 */
export const licenceChangeErrorMessage = (code: string | undefined, licence?: string, preview = false) =>
	i18n.t(`${LICENCE_CHANGE_KEYS}.errors.${code}`, {
		licence,
		defaultValue: i18n.t(`${LICENCE_CHANGE_KEYS}.errors.${preview ? 'preview' : 'generic'}`)
	});

/**
 * Preview (`preview: true`) or apply a licence change on an existing organisation.
 * Rejects with the API's error `code`; see licenceChangeErrorMessage.
 */
export const changeOrganisationLicence = createAsyncThunk<
	LicenceChangeSummary,
	{
		organisationId: string;
		licenseId: string;
		accessMode?: LicenceAccessMode;
		allowanceMode?: LicenceAllowanceMode;
		preview?: boolean;
	},
	{ dispatch: AppDispatch; state: RootState }
>('admin/changeOrganisationLicence', async ({ organisationId, ...body }, { rejectWithValue }) => {
	try {
		return await API.put('inspirusapi', `/admins/organisations/${organisationId}/license`, {
			headers: {},
			body
		});
	} catch (error: unknown) {
		console.error('Error in changeOrganisationLicence:', error);
		return rejectWithValue((error as any)?.response?.data?.code);
	}
});

/** PTEC-524: billing ledger — billable lines reference leadId only (no PII). */
export interface LeadShareLedgerLine {
	leadId: string;
	billingPeriod: string;
	status: 'DELIVERED' | 'BILLED';
	priceApplied: number;
	currency: LeadShareCurrency;
	deliveredAt?: string;
}

export interface LeadShareLedgerPeriod {
	period: string;
	currency: LeadShareCurrency;
	leadCount: number;
	gross: number;
}

/**
 * PTEC-524: per-lead revenue ledger. The FIXED ANNUAL platform fee sits
 * outside leads and outside this ledger — commercial home (enrolment vs the
 * licence model, PTEC-555) is an open decision on the ticket.
 */
export interface LeadShareLedger {
	organisationId: string;
	periods: LeadShareLedgerPeriod[];
	lines: LeadShareLedgerLine[];
	generatedAt: string;
}

export const getLeadShareEnrollment = createAsyncThunk<
	LeadShareEnrollment,
	{ organisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getLeadShareEnrollment', async ({ organisationId }, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `/admins/organisations/${organisationId}/leadshare-enrollment`, {
			headers: {},
			response: true
		});

		return response.data;
	} catch (error: unknown) {
		console.error('Error in getLeadShareEnrollment:', error);
		return rejectWithValue('Failed to get lead-share enrollment config.');
	}
});

export interface LeadShareBillingCurrencyRollup {
	currency: LeadShareCurrency;
	allTimeLeads: number;
	thisMonthLeads: number;
	allTimeRevenue: number;
	thisMonthRevenue: number;
}

export interface LeadShareBillingRollup {
	billableLeads: number;
	latestPeriod: string | null;
	currencies: LeadShareBillingCurrencyRollup[];
}

export interface LeadShareBillingPartner extends LeadShareBillingRollup {
	organisationId: string;
	name: string;
}

/** PTEC-524: billing dashboard — per-partner rollups + per-currency platform totals. */
export interface LeadShareBillingSummary {
	partners: LeadShareBillingPartner[];
	totals: LeadShareBillingRollup;
	activePartners: number;
	nowPeriod: string;
	generatedAt: string;
}

export const getLeadShareLedger = createAsyncThunk<
	LeadShareLedger,
	{ organisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getLeadShareLedger', async ({ organisationId }, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `/admins/organisations/${organisationId}/leadshare-ledger`, {
			headers: {},
			response: true
		});
		return response.data;
	} catch (error: unknown) {
		console.error('Error in getLeadShareLedger:', error);
		return rejectWithValue('Failed to load the billing ledger.');
	}
});

export const getLeadShareBillingSummary = createAsyncThunk<
	LeadShareBillingSummary,
	void,
	{ dispatch: AppDispatch; state: RootState }
>('admin/getLeadShareBillingSummary', async (_, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', '/admins/leadshare-billing', {
			headers: {},
			response: true
		});
		return response.data;
	} catch (error: unknown) {
		console.error('Error in getLeadShareBillingSummary:', error);
		return rejectWithValue('Failed to load the billing summary.');
	}
});

export const updateLeadShareEnrollment = createAsyncThunk<
	LeadShareEnrollment,
	{ organisationId: string; config: LeadShareEnrollmentUpdate },
	{ dispatch: AppDispatch; state: RootState }
>('admin/updateLeadShareEnrollment', async ({ organisationId, config }, { rejectWithValue }) => {
	try {
		const response = await API.put('inspirusapi', `/admins/organisations/${organisationId}/leadshare-enrollment`, {
			headers: {},
			body: config
		});

		return response;
	} catch (error: unknown) {
		console.error('Error in updateLeadShareEnrollment:', error);
		const validationErrors = (error as any)?.response?.data?.errors;
		return rejectWithValue(Array.isArray(validationErrors) ? validationErrors : undefined);
	}
});

// Enrolled partners grid — orgs with a PARTNERENROLLMENT record, any status (PTEC-549).
export const getPartners = createAsyncThunk<LazyListResponse<any>, void, { dispatch: AppDispatch; state: RootState }>(
	'admin/getPartners',
	async () => {
		const response = await API.get('inspirusapi', '/admins/partners', {
			headers: {},
			response: true
		});

		return { ...response.data, next: null };
	}
);

// Enrol an organisation as a partner and issue grant features (PTEC-549).
export const enrolPartner = createAsyncThunk<
	{ enrollment: { status: string }; features: string[] },
	{ organisationId: string; features: string[] },
	{ dispatch: AppDispatch; state: RootState }
>('admin/enrolPartner', async ({ organisationId, features }, { rejectWithValue }) => {
	try {
		const response = await API.post('inspirusapi', `/admins/organisations/${organisationId}/enrolment`, {
			headers: {},
			body: { features }
		});

		return response;
	} catch (error: unknown) {
		console.error('Error in enrolPartner:', error);
		const validationErrors = (error as any)?.response?.data?.errors;
		return rejectWithValue(Array.isArray(validationErrors) ? validationErrors : undefined);
	}
});

export const getOrganisationGrants = createAsyncThunk<
	any,
	{ organisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getOrganisationGrants', async ({ organisationId }) => {
	try {
		const response = await API.get('inspirusapi', `/admins/organisations/${organisationId}`, {
			queryStringParameters: {},
			headers: {},
			response: true
		});

		return response.data;
	} catch (error: unknown) {
		return 'Failed to get organization grants.';
	}
});

export const addDigitalProduct = createAsyncThunk<
	any,
	{ product: Product },
	{ dispatch: AppDispatch; state: RootState }
>('admin/addDigitalProduct', async ({ product }) => {
	try {
		const response = await API.post('inspirusapi', '/admins/products', {
			headers: {},
			body: product
		});

		return response;
	} catch (error: unknown) {
		return 'Failed to add digital product.';
	}
});

export interface CalendlyEventType {
	calendlyUri: string;
	name: string;
	slug: string;
	description: string;
	duration: number;
	schedulingUrl: string;
	kind: string;
	color: string;
}

export const fetchCalendlyEventTypes = createAsyncThunk<
	CalendlyEventType[],
	void,
	{ dispatch: AppDispatch; state: RootState }
>('admin/fetchCalendlyEventTypes', async () => {
	const response = await API.get('inspirusapi', '/advisor/admin/calendly-event-types', {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return response.data.eventTypes;
});

export const addDigitalCoupon = createAsyncThunk<any, { coupon: Coupon }, { dispatch: AppDispatch; state: RootState }>(
	'admin/addDigitalCoupon',
	async ({ coupon }) => {
		try {
			const response = await API.post('inspirusapi', '/admins/coupons', {
				headers: {},
				body: coupon
			});

			return response;
		} catch (error: unknown) {
			return 'Failed to add digital coupon.';
		}
	}
);

export const resetUserMFA = createAsyncThunk<any, { id: string }, { dispatch: AppDispatch; state: RootState }>(
	'admin/resetUserMFA',
	async ({ id }) => {
		try {
			const response = await API.post('inspirusapi', '/admins/resetMFA', {
				headers: {},
				body: { id: id }
			});

			return response;
		} catch (error: unknown) {
			return 'Failed to reset user MFA.';
		}
	}
);

export const setTempPassword = createAsyncThunk<
	any,
	{ userId: string; tempPassword: string; organisationId?: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/setTempPassword', async ({ userId, tempPassword, organisationId }, { rejectWithValue }) => {
	try {
		const endpoint = organisationId
			? `/organisations/${organisationId}/setTempPassword`
			: '/admins/setTempPassword';
		const response = await API.post('inspirusapi', endpoint, {
			headers: {},
			body: { userId, tempPassword }
		});
		return response;
	} catch (error: unknown) {
		// Rejected with the API's `code`, not returned: a returned message ran the success
		// reducer, so a password Cognito refused was reported as set (PTEC-1039).
		return rejectWithValue((error as any)?.response?.data?.code);
	}
});

const TEMP_PASSWORD_KEYS = 'pages.admin.organisations.tempPassword';

/** The translated reason a temporary password was not set, keyed on the API's `code`. */
export const tempPasswordErrorMessage = (code?: string) =>
	i18n.t(`${TEMP_PASSWORD_KEYS}.errors.${code}`, {
		defaultValue: i18n.t(`${TEMP_PASSWORD_KEYS}.errors.generic`)
	});

const USER_ACCOUNT_KEYS = 'pages.admin.users.account';

/** The translated message for a refused resend, keyed on the API's `code`. */
export const resendInviteErrorMessage = (code?: string) =>
	i18n.t(`${USER_ACCOUNT_KEYS}.resendInvite.errors.${code}`, {
		defaultValue: i18n.t(`${USER_ACCOUNT_KEYS}.resendInvite.errors.generic`)
	});

/** Whether a user has ever signed in and, if invited, when their invitation expires. */
export const getUserAccount = createAsyncThunk<
	UserAccount,
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/getUserAccount', async ({ userId }) => ({
	...(await API.get('inspirusapi', `/admins/users/${userId}/account`, { headers: {} })),
	userId
}));

/**
 * Sends an invited user who never signed in a new invitation and temporary password.
 * Rejects with the API's error `code`; see resendInviteErrorMessage.
 */
export const resendUserInvite = createAsyncThunk<
	UserAccount,
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/resendUserInvite', async ({ userId }, { rejectWithValue }) => {
	try {
		return {
			...(await API.post('inspirusapi', `/admins/users/${userId}/resend-invite`, { headers: {}, body: {} })),
			userId
		};
	} catch (error: unknown) {
		console.error('Error in resendUserInvite:', error);
		return rejectWithValue((error as any)?.response?.data?.code);
	}
});

/** The translated message for a failed ledger retry, keyed on the API's `code`. */
export const retryLedgerErrorMessage = (code?: string) =>
	i18n.t(`${USER_ACCOUNT_KEYS}.ledger.errors.${code}`, {
		defaultValue: i18n.t(`${USER_ACCOUNT_KEYS}.ledger.errors.generic`)
	});

/**
 * Registers a user on the ledger now, or records what the ledger already holds for them. For
 * users whose background registration failed every retry.
 */
export const retryLedgerRegistration = createAsyncThunk<
	{ userId: string; ledger: UserLedger },
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/retryLedgerRegistration', async ({ userId }, { rejectWithValue }) => {
	try {
		return {
			userId,
			ledger: await API.post('inspirusapi', `/admins/users/${userId}/ledger/retry`, { headers: {}, body: {} })
		};
	} catch (error: unknown) {
		console.error('Error in retryLedgerRegistration:', error);
		return rejectWithValue((error as any)?.response?.data?.code);
	}
});

/** The translated message for a failed Mailchimp retry, keyed on the API's `code`. */
export const retryMailchimpErrorMessage = (code?: string) =>
	i18n.t(`${USER_ACCOUNT_KEYS}.mailchimp.errors.${code}`, {
		defaultValue: i18n.t(`${USER_ACCOUNT_KEYS}.mailchimp.errors.generic`)
	});

/** Adds a user to the Mailchimp audience with the Wallet tag, as their creation would have. */
export const retryMailchimp = createAsyncThunk<
	{ userId: string; mailchimp: UserMailchimp },
	{ userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/retryMailchimp', async ({ userId }, { rejectWithValue }) => {
	try {
		return {
			userId,
			mailchimp: await API.post('inspirusapi', `/admins/users/${userId}/mailchimp/retry`, {
				headers: {},
				body: {}
			})
		};
	} catch (error: unknown) {
		console.error('Error in retryMailchimp:', error);
		return rejectWithValue((error as any)?.response?.data?.code);
	}
});

export const editDigitalProduct = createAsyncThunk<
	any,
	{ product: Product; id: string },
	{ dispatch: AppDispatch; state: RootState }
>('admin/editDigitalProduct', async ({ product, id }) => {
	try {
		const response = await API.put('inspirusapi', `/admins/products/${id}`, {
			headers: {},
			body: product
		});

		return response;
	} catch (error: unknown) {
		return 'Failed to add digital product.';
	}
});

export const deleteDigitalProduct = createAsyncThunk<any, { id: string }, { dispatch: AppDispatch; state: RootState }>(
	'admin/deleteDigitalProduct',
	async ({ id }) => {
		try {
			const response = await API.del('inspirusapi', `/admins/products/${id}`, {
				headers: {},
				body: {}
			});

			return response;
		} catch (error: unknown) {
			return 'Failed to delete digital product.';
		}
	}
);

export const deleteDigitalCoupon = createAsyncThunk<
	any,
	{ dynamoId: string | undefined },
	{ dispatch: AppDispatch; state: RootState }
>('admin/deleteDigitalCoupon', async ({ dynamoId }) => {
	try {
		const response = await API.del('inspirusapi', `/admins/coupons/${dynamoId}`, {
			headers: {},
			body: {}
		});

		return response;
	} catch (error: unknown) {
		return 'Failed to delete digital coupon.';
	}
});

const adminSlice = createSlice({
	name: 'admin',
	initialState,
	reducers: {
		resetAdmin(state) {
			state = initialState;
			return state;
		},
		updatePearsanaRanAdminRow(state, action) {
			const { id, pearsanaRan } = action.payload;
			const org = state.organisations.items.find((org) => org.id === id);
			if (org) {
				org.pearsanaRan = pearsanaRan;
			}
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, addDigitalProduct, 'addDigitalProductStatus', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.addProduct.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.addProduct.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, addDigitalCoupon, 'addDigitalCouponStatus', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalCoupons.createCouponSuccess'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalCoupons.createCouponFailure'), {
					variant: 'error'
				});
			}
		});

		buildReducers(builder, resetUserMFA, 'resetUserMFAStatus', {
			onFulfilled: (state) => {
				state.isLoading = false;
				enqueueSnackbar(i18n.t('pages.admin.organisations.mfa.updateSuccessMessage'), {
					variant: 'success'
				});
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.mfa.updateErrorMessage'), { variant: 'error' });
				state.isLoading = false;
			}
		});
		// A refusal is shown in the dialog, which stays open with the password, so it has no toast.
		buildReducers(builder, setTempPassword, 'setTempPasswordStatus', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.tempPassword.successMessage'), {
					variant: 'success'
				});
			}
		});
		// Only the latest request counts. An admin who moves from one user to the next before the
		// first answer arrives must never see that user's account, or act on it, on the second
		// user's page.
		builder.addCase(getUserAccount.pending, (state, action) => {
			state.userAccountRequestId = action.meta.requestId;
			state.userAccountStatus.status = RequestStatus.PENDING;
			state.userAccountStatus.error = null;
			state.userAccount = null;
		});
		builder.addCase(getUserAccount.fulfilled, (state, action) => {
			if (action.meta.requestId !== state.userAccountRequestId) return;
			state.userAccountStatus.status = RequestStatus.SUCCESS;
			state.userAccount = action.payload;
		});
		builder.addCase(getUserAccount.rejected, (state, action) => {
			if (action.meta.requestId !== state.userAccountRequestId) return;
			state.userAccountStatus.status = RequestStatus.ERROR;
			state.userAccountStatus.error = action.error;
		});
		buildReducers(builder, resendUserInvite, 'resendUserInviteStatus', {
			onFulfilled: (state, payload) => {
				// Only if the page still shows this user: the admin may have moved on to someone else.
				if (state.userAccount?.userId === payload.userId) {
					// A resend says nothing about the ledger or Mailchimp, so what is known of them is kept.
					state.userAccount = {
						...payload,
						ledger: state.userAccount?.ledger ?? payload.ledger,
						mailchimp: state.userAccount?.mailchimp ?? payload.mailchimp
					};
				}
				enqueueSnackbar(i18n.t(`${USER_ACCOUNT_KEYS}.resendInvite.success`, { email: payload.email }), {
					variant: 'success'
				});
			},
			onRejected: (state, error, code) => {
				enqueueSnackbar(resendInviteErrorMessage(typeof code === 'string' ? code : undefined), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, retryMailchimp, 'retryMailchimpStatus', {
			onFulfilled: (state, payload) => {
				if (state.userAccount?.userId === payload.userId) state.userAccount.mailchimp = payload.mailchimp;
				enqueueSnackbar(i18n.t(`${USER_ACCOUNT_KEYS}.mailchimp.success`), { variant: 'success' });
			},
			onRejected: (state, error, code) => {
				enqueueSnackbar(retryMailchimpErrorMessage(typeof code === 'string' ? code : undefined), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, retryLedgerRegistration, 'retryLedgerStatus', {
			onFulfilled: (state, payload) => {
				if (state.userAccount?.userId === payload.userId) state.userAccount.ledger = payload.ledger;
				enqueueSnackbar(
					i18n.t(`${USER_ACCOUNT_KEYS}.ledger.${payload.ledger.created ? 'success' : 'alreadyRegistered'}`),
					{ variant: 'success' }
				);
			},
			onRejected: (state, error, code) => {
				enqueueSnackbar(retryLedgerErrorMessage(typeof code === 'string' ? code : undefined), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, editDigitalProduct, 'editDigitalProductStatus', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.editProduct.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.editProduct.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, deleteDigitalProduct, 'deleteDigitalProductStatus', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.deleteProduct.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.deleteProduct.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, deleteDigitalCoupon, 'deleteDigitalCouponStatus', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.deleteProduct.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.digitalProducts.products.deleteProduct.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, getOrganisationGrants, 'organisationGrantsRequestStatus', {
			onFulfilled: (state, payload) => {
				state.organisationGrants = payload;
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.grants.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, getOrganisations, 'organisationRequestStatus', {
			onFulfilled: (state, payload) => {
				const organisations = state.organisations;
				organisations.items = payload.items;
				organisations.totalCount = payload.totalCount;
				organisations.next = payload.next;
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, getPartnerEligibleOrganisations, 'organisationRequestStatus', {
			onFulfilled: (state, payload) => {
				const organisations = state.organisations;
				organisations.items = payload.items;
				organisations.totalCount = payload.totalCount;
				organisations.next = payload.next;
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, getPartners, 'partnersRequestStatus', {
			onFulfilled: (state, payload) => {
				state.partners.items = payload.items;
				state.partners.totalCount = payload.totalCount;
				state.partners.next = payload.next;
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, getOrganisationOrderHistory, 'organisationOrderHistoryRequestStatus', {
			onFulfilled: (state, payload) => {
				state.organisationOrderHistory = payload;
			}
		});
		buildReducers(builder, getOrderHistory, 'orderHistoryRequestStatus', {
			onFulfilled: (state, payload) => {
				state.orderHistory = payload;
			}
		});
		buildReducers(builder, updateOrganisationGrant, 'updateOrganisationGrantStatus', {
			onFulfilled: (state, payload) => {
				state.isLoading = false;
				enqueueSnackbar(i18n.t('pages.admin.organisations.grants.updateSuccessMessage'), {
					variant: 'success'
				});

				// The response is the grants record as written. Without it the page kept
				// showing the old wallet total until it was reloaded.
				const current = state.organisationGrants?.grants?.[0];
				if (!current || !payload || typeof payload !== 'object' || payload.id !== current.id) return;
				const grant = { ...current, ...payload };
				state.organisationGrants.grants[0] = grant;
				const used = state.organisationGrants.usedWalletCount;
				if (typeof used === 'number') {
					state.organisationGrants.remainingWalletCount = Math.max(
						(Number(grant.walletCount) || 0) - used,
						0
					);
				}
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.grants.updateErrorMessage'), { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, changeOrganisationLicence, 'changeOrganisationLicenceStatus', {
			onFulfilled: (state, payload) => {
				// A preview changes nothing.
				if (payload.preview) return;

				enqueueSnackbar(
					i18n.t(`${LICENCE_CHANGE_KEYS}.success.${payload.applied ? 'applied' : 'scheduled'}`, {
						licence: payload.to.license
					}),
					{ variant: 'success' }
				);
				if (payload.roleMoveFailed) {
					enqueueSnackbar(i18n.t(`${LICENCE_CHANGE_KEYS}.roleMoveFailed`), {
						variant: 'warning',
						persist: true
					});
				}

				// The response carries the grants record as written, used directly because a
				// re-read through the index can lag behind the write.
				if (!payload.grants || !state.organisationGrants?.grants?.[0]) return;
				state.organisationGrants.grants[0] = payload.grants;
				if (payload.applied && state.organisationGrants.organisation?.Item) {
					state.organisationGrants.organisation.Item.license = payload.to.license;
				}
				// The wallets left follow the new allowance; the number in use has not moved.
				const used = state.organisationGrants.usedWalletCount;
				if (payload.applied && typeof used === 'number') {
					state.organisationGrants.remainingWalletCount = Math.max(
						(Number(payload.grants.walletCount) || 0) - used,
						0
					);
				}
			}
		});
		buildReducers(builder, updateOrderInvoiceStatus, 'updateOrderInvoiceStatus', {});
		buildReducers(builder, getLeadShareEnrollment, 'leadShareEnrollmentRequestStatus', {
			onFulfilled: (state, payload) => {
				state.leadShareEnrollment = payload;
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.admin.organisations.leadShareEnrollment.loadErrorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, getLeadShareLedger, 'leadShareLedgerRequestStatus', {
			onFulfilled: (state, payload) => {
				state.leadShareLedger = payload;
			}
		});
		buildReducers(builder, getLeadShareBillingSummary, 'leadShareBillingSummaryRequestStatus', {
			onFulfilled: (state, payload) => {
				state.leadShareBillingSummary = payload;
			}
		});
		buildReducers(builder, updateLeadShareEnrollment, 'updateLeadShareEnrollmentStatus', {
			onFulfilled: (state, payload) => {
				state.leadShareEnrollment = payload;
				// Keep the Configuration table row in step with the saved enrollment.
				const org = state.organisations.items.find((item: any) => item.id === payload.organisationId);
				if (org?.leadShare) {
					org.leadShare = {
						configured: true,
						enabled: payload.enabled,
						fieldSet: payload.fieldSet,
						pricePerLead: payload.pricePerLead,
						currency: payload.currency,
						updatedAt: payload.updatedAt ?? null,
						updatedBy: payload.updatedBy ?? null
					};
				}
				enqueueSnackbar(i18n.t('pages.admin.organisations.leadShareEnrollment.updateSuccessMessage'), {
					variant: 'success'
				});
			},
			onRejected: (state, error, detailedErrorFromAPI) => {
				const detail = Array.isArray(detailedErrorFromAPI) ? detailedErrorFromAPI.join(' ') : undefined;
				enqueueSnackbar(detail ?? i18n.t('pages.admin.organisations.leadShareEnrollment.updateErrorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, getOrderInvoicePDF, 'orderInvoicePDFRequestStatus', {
			onFulfilled: (state, payload) => {
				state.orderInvoicePDF = payload;
			},
			onRejected: () => {
				enqueueSnackbar(
					i18n.t('pages.admin.digitalProducts.masterBiller.orderHistory.orders.invoicePDFError'),
					{
						variant: 'error'
					}
				);
			}
		});
	}
});

export const { resetAdmin, updatePearsanaRanAdminRow } = adminSlice.actions;

export default adminSlice.reducer;
